import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, Copy, Clock, ArrowRight, Shield } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CACHE_KEYS = {
  email: 'skinkosh_user_email',
  wallet: 'skinkosh_user_wallet',
  telegram: 'skinkosh_user_telegram'
};

function ExchangePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    fromCurrency, 
    fromCurrencyName,
    fromCurrencySymbol,
    fromCurrencyNetwork,
    toCurrency, 
    toCurrencyName,
    toCurrencySymbol,
    toCurrencyNetwork,
    fromAmount, 
    toAmount,
    commissionPercent
  } = location.state || {};

  const [email, setEmail] = useState(() => localStorage.getItem(CACHE_KEYS.email) || "");
  const [wallet, setWallet] = useState("");
  const [telegram, setTelegram] = useState(() => localStorage.getItem(CACHE_KEYS.telegram) || "");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [depositAddress, setDepositAddress] = useState("");
  const [requestId, setRequestId] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [timeLeft, setTimeLeft] = useState(1800);
  const [requestStatus, setRequestStatus] = useState("pending");
  const [walletError, setWalletError] = useState("");
  const [walletValid, setWalletValid] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (toCurrency) {
      const cachedWallet = localStorage.getItem(`${CACHE_KEYS.wallet}_${toCurrency}`);
      if (cachedWallet) {
        setWallet(cachedWallet);
        validateWallet(cachedWallet);
      }
    }
  }, [toCurrency]);

  const validateWallet = async (address) => {
    if (!address || address.length < 10) {
      setWalletError("");
      setWalletValid(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/validate-address`, null, {
        params: { address, currency_id: toCurrency }
      });
      
      if (response.data.valid) {
        setWalletError("");
        setWalletValid(true);
      } else {
        setWalletError(response.data.error);
        setWalletValid(false);
      }
    } catch (error) {
      console.error("Validation error:", error);
      setWalletValid(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (wallet) validateWallet(wallet);
    }, 500);
    return () => clearTimeout(timer);
  }, [wallet, toCurrency]);

  useEffect(() => {
    if (step === "payment" && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            toast.error("Время истекло! Заявка отменена");
            setTimeout(() => navigate("/"), 2000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft, navigate]);

  useEffect(() => {
    if (step === "payment" && requestId) {
      let ws = null;
      let reconnectTimeout = null;
      
      const connectWebSocket = () => {
        const wsUrl = BACKEND_URL.replace('http', 'ws').replace('https', 'wss');
        ws = new WebSocket(`${wsUrl}/ws/request/${requestId}`);
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "status") setRequestStatus(data.status);
            if (data.status === "completed") {
              setStep("success");
              toast.success("Обмен завершен!");
            } else if (data.status === "cancelled") {
              toast.error("Заявка отменена");
              setTimeout(() => navigate("/"), 2000);
            }
          } catch (e) {}
        };
        
        ws.onclose = () => {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        };
      };
      
      connectWebSocket();
      
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/exchange-requests/${requestId}`);
          const newStatus = response.data.status;
          setRequestStatus(newStatus);
          if (newStatus === "completed") {
            clearInterval(interval);
            setStep("success");
          } else if (newStatus === "cancelled") {
            clearInterval(interval);
            toast.error("Заявка отменена");
            setTimeout(() => navigate("/"), 2000);
          }
        } catch (error) {}
      }, 10000);
      
      return () => {
        if (ws) ws.close();
        if (reconnectTimeout) clearTimeout(reconnectTimeout);
        clearInterval(interval);
      };
    }
  }, [step, requestId, navigate]);

  useEffect(() => {
    if (!fromCurrency || !toCurrency) navigate("/");
  }, [fromCurrency, toCurrency, navigate]);

  if (!fromCurrency || !toCurrency) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-500">Загрузка...</div>
      </div>
    );
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) errors.email = "Email обязателен";
    else if (!emailRegex.test(email)) errors.email = "Введите корректный email";
    if (!wallet) errors.wallet = "Введите адрес кошелька";
    else if (walletError) errors.wallet = walletError;
    else if (!walletValid) errors.wallet = "Адрес не прошёл проверку";
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Исправьте ошибки");
      return;
    }

    localStorage.setItem(CACHE_KEYS.email, email);
    localStorage.setItem(`${CACHE_KEYS.wallet}_${toCurrency}`, wallet);
    if (telegram) localStorage.setItem(CACHE_KEYS.telegram, telegram);

    setLoading(true);
    try {
      const response = await axios.post(`${API}/exchange-requests`, {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        from_amount: fromAmount,
        to_amount: toAmount,
        user_email: email,
        user_wallet: wallet,
        user_telegram: telegram || null
      });
      
      setDepositAddress(response.data.deposit_address);
      setRequestId(response.data.id);
      setOrderNumber(response.data.order_number || response.data.id.slice(-8).toUpperCase());
      setStep("payment");
      toast.success("Заявка создана!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания заявки");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/exchange-requests/${requestId}/confirm-payment`);
      setStep("success");
      toast.success("Оплата подтверждена!");
    } catch (error) {
      toast.error("Ошибка подтверждения");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Скопировано");
  };

  // Header Component
  const Header = () => (
    <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        <Button onClick={() => navigate("/")} variant="ghost" size="icon" className="hover:bg-blue-50">
          <ArrowLeft size={24} className="text-slate-600" />
        </Button>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <span className="text-white font-black text-xl">S</span>
          </div>
          <span className="font-bold text-xl text-slate-800">SKIN<span className="text-blue-600">KOSH</span></span>
        </Link>
      </div>
    </header>
  );

  // SUCCESS STEP
  if (step === "success") {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-200">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="text-green-600" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Оплата подтверждена</h2>
            <p className="text-slate-500 mb-6">
              Ваша заявка отправлена на проверку. После подтверждения мы переведем <span className="font-semibold text-blue-600">{toAmount} {toCurrencySymbol}</span> на ваш кошелек.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-500">Номер заявки</p>
              <p className="font-mono text-2xl font-bold text-blue-600">#{orderNumber}</p>
            </div>
            <Button onClick={() => navigate("/")} className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold">
              Вернуться на главную
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // PAYMENT STEP
  if (step === "payment") {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Timer Bar */}
            <div className={`px-6 py-4 flex items-center justify-between ${timeLeft < 300 ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}>
              <div className="flex items-center gap-3 text-white">
                <Clock size={24} />
                <span className="font-semibold">Время на оплату</span>
              </div>
              <span className="font-mono text-3xl font-bold text-white">{formatTime(timeLeft)}</span>
            </div>

            <div className="p-6 space-y-6">
              {/* Exchange Summary */}
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                <h3 className="font-semibold text-slate-700 mb-4">Детали обмена</h3>
                <div className="flex items-center justify-between gap-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 mb-1">Отправляете</p>
                    <p className="text-xl font-bold text-slate-800">{fromAmount}</p>
                    <p className="text-sm text-blue-600 font-semibold">{fromCurrencySymbol}</p>
                    <p className="text-xs text-slate-400">{fromCurrencyNetwork}</p>
                  </div>
                  <ArrowRight className="text-blue-500" size={32} />
                  <div className="text-center">
                    <p className="text-xs text-slate-500 mb-1">Получаете</p>
                    <p className="text-xl font-bold text-green-600">{toAmount}</p>
                    <p className="text-sm text-green-600 font-semibold">{toCurrencySymbol}</p>
                    <p className="text-xs text-slate-400">{toCurrencyNetwork}</p>
                  </div>
                </div>
              </div>

              {/* Your Wallet */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle2 size={18} />
                  <span className="font-semibold text-sm">Ваш кошелек для получения</span>
                </div>
                <p className="font-mono text-sm text-slate-700 break-all bg-white rounded-lg p-3 border border-green-200">{wallet}</p>
              </div>

              {/* Deposit Address */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5">
                <h3 className="font-bold text-blue-700 mb-4 text-center">
                  Отправьте {fromAmount} {fromCurrencySymbol} на адрес:
                </h3>
                
                <div className="bg-white rounded-xl p-4 mb-4 flex justify-center">
                  <QRCodeSVG value={depositAddress} size={180} level="H" includeMargin={true} />
                </div>
                
                <div className="bg-white rounded-lg p-4 mb-4 border border-blue-200">
                  <p className="font-mono text-sm text-slate-800 break-all text-center">{depositAddress}</p>
                </div>
                
                <Button onClick={() => copyToClipboard(depositAddress)} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  <Copy size={18} className="mr-2" /> Скопировать адрес
                </Button>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Важно:</strong> Отправьте точно <strong>{fromAmount} {fromCurrencySymbol}</strong> в сети <strong>{fromCurrencyNetwork}</strong>. Ошибка в сумме или сети может привести к потере средств.
                </p>
              </div>

              {/* Instructions */}
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-700">Инструкция:</h4>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Скопируйте адрес выше</li>
                  <li>Откройте кошелек и отправьте {fromAmount} {fromCurrencySymbol}</li>
                  <li>Выберите сеть {fromCurrencyNetwork}</li>
                  <li>После отправки нажмите «Я оплатил»</li>
                </ol>
              </div>

              {/* Confirm Button */}
              <Button onClick={handleConfirmPayment} disabled={loading} className="w-full h-14 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg shadow-lg">
                {loading ? "Обработка..." : "Я оплатил"}
              </Button>
              
              <p className="text-center text-sm text-slate-500">
                Заявка #{orderNumber}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // FORM STEP
  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-5">
            <h2 className="text-xl font-bold text-white">Оформление заявки</h2>
            <p className="text-blue-100 text-sm mt-1">Заполните данные для обмена</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Exchange Summary */}
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <div className="flex items-center justify-between gap-4">
                <div className="text-center flex-1">
                  <p className="text-xs text-slate-500 mb-1">Отправляете</p>
                  <p className="text-2xl font-bold text-slate-800">{fromAmount}</p>
                  <p className="text-blue-600 font-semibold">{fromCurrencySymbol}</p>
                  <p className="text-xs text-slate-400 mt-1">{fromCurrencyNetwork}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <ArrowRight className="text-blue-600" size={20} />
                </div>
                <div className="text-center flex-1">
                  <p className="text-xs text-slate-500 mb-1">Получаете</p>
                  <p className="text-2xl font-bold text-green-600">{toAmount}</p>
                  <p className="text-green-600 font-semibold">{toCurrencySymbol}</p>
                  <p className="text-xs text-slate-400 mt-1">{toCurrencyNetwork}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFormErrors(p => ({...p, email: ''})); }}
                  placeholder="your@email.com"
                  className={`h-12 bg-slate-50 border-slate-200 ${formErrors.email ? 'border-red-500 border-2' : ''}`}
                />
                {formErrors.email && <p className="text-red-500 text-sm mt-1">⚠️ {formErrors.email}</p>}
                <p className="text-xs text-slate-400 mt-1">Для уведомлений о статусе</p>
              </div>

              {/* Wallet */}
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">
                  Кошелек {toCurrencySymbol} <span className="text-red-500">*</span>
                  <span className="text-xs text-slate-400 ml-1">({toCurrencyNetwork})</span>
                </Label>
                <Input
                  value={wallet}
                  onChange={(e) => { setWallet(e.target.value); setFormErrors(p => ({...p, wallet: ''})); }}
                  placeholder="Адрес кошелька"
                  className={`h-12 bg-slate-50 border-slate-200 font-mono text-sm ${
                    formErrors.wallet || walletError ? 'border-red-500 border-2' : 
                    walletValid ? 'border-green-500 border-2' : ''
                  }`}
                />
                {(formErrors.wallet || walletError) && <p className="text-red-500 text-sm mt-1">⚠️ {formErrors.wallet || walletError}</p>}
                {walletValid && <p className="text-green-600 text-sm mt-1">✓ Адрес валиден</p>}
              </div>

              {/* Telegram */}
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">
                  Telegram <span className="text-xs text-slate-400">(необязательно)</span>
                </Label>
                <Input
                  value={telegram}
                  onChange={(e) => setTelegram(e.target.value)}
                  placeholder="@username"
                  className="h-12 bg-slate-50 border-slate-200"
                />
              </div>

              {/* Errors */}
              {Object.keys(formErrors).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 font-semibold text-sm">Исправьте ошибки:</p>
                  {Object.values(formErrors).filter(e => e).map((e, i) => (
                    <p key={i} className="text-red-500 text-sm">• {e}</p>
                  ))}
                </div>
              )}

              {/* Submit */}
              <Button type="submit" disabled={loading} className="w-full h-14 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold text-lg shadow-lg shadow-blue-500/30">
                {loading ? "Создание..." : "Создать заявку"}
              </Button>
            </form>

            {/* Security Note */}
            <div className="flex items-center gap-2 justify-center text-slate-400 text-xs">
              <Shield size={14} />
              <span>Безопасное соединение SSL</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExchangePage;
